/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Subscriptions and consent", description = "Subscription topics, unsubscribes, double opt-in, SMS STOP handling, and suppression lists.", lastUpdatedDate = new Date(1788998400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Subscriptions and consent",
        description: "Subscription topics, unsubscribes, double opt-in, SMS STOP handling, and suppression lists.",
        lastUpdatedDate: "Thu Sep 10 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Subscription topics",
          "depth": 0,
          "id": "subscription-topics"
        }, {
          "value": "Marketing and transactional messages",
          "depth": 0,
          "id": "marketing-and-transactional-messages"
        }, {
          "value": "When email can't be delivered",
          "depth": 0,
          "id": "when-email-cant-be-delivered"
        }, {
          "value": "How recipients unsubscribe",
          "depth": 0,
          "id": "how-recipients-unsubscribe"
        }, {
          "value": "Unsubscribe link variables",
          "depth": 1,
          "id": "unsubscribe-link-variables"
        }, {
          "value": "Keep your own consent platform",
          "depth": 0,
          "id": "keep-your-own-consent-platform"
        }, {
          "value": "Double opt-in",
          "depth": 0,
          "id": "double-opt-in"
        }, {
          "value": "Suppression lists",
          "depth": 0,
          "id": "suppression-lists"
        }, {
          "value": "What recipients can't do",
          "depth": 0,
          "id": "what-recipients-cant-do"
        }, {
          "value": "SMS consent",
          "depth": 0,
          "id": "sms-consent"
        }, {
          "value": "Topics are always opt-in",
          "depth": 1,
          "id": "topics-are-always-opt-in"
        }, {
          "value": "STOP and START",
          "depth": 1,
          "id": "stop-and-start"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      em: "em",
      h2: "h2",
      ul: "ul",
      li: "li",
      code: "code",
      h3: "h3",
      pre: "pre",
      span: "span",
      a: "a"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, lifecycle and CRM teams, compliance"
            })]
          })
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Subscription topics let recipients choose which email they receive. Hightouch enforces those choices on every send and records each change in your warehouse."
        })
      }), "\n", _jsx(_components.h2, {
        id: "subscription-topics",
        children: "Subscription topics"
      }), "\n", _jsx(_components.p, {
        children: "A topic is a category a recipient can subscribe to or leave, such as a weekly newsletter or promotions. Every marketing message is sent under one topic. Leaving Promotions doesn't stop the newsletter."
      }), "\n", _jsxs(_components.p, {
        children: ["Add topics under ", _jsx(_components.strong, {
          children: "Settings → Channels"
        }), ". Each topic has a policy:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Policy"
            }), _jsx(_components.th, {
              children: "Default state for a new recipient"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Opt-out"
              })
            }), _jsx(_components.td, {
              children: "Subscribed. They receive the topic until they unsubscribe."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Opt-in"
              })
            }), _jsx(_components.td, {
              children: "Not subscribed. They receive nothing until they subscribe."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Double opt-in"
              })
            }), _jsx(_components.td, {
              children: "Not subscribed. They must confirm from a message before you can send."
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "A recipient can also unsubscribe from email altogether. A channel unsubscribe stops all marketing email regardless of topic."
      }), "\n", _jsx(_components.h2, {
        id: "marketing-and-transactional-messages",
        children: "Marketing and transactional messages"
      }), "\n", _jsx(_components.p, {
        children: "Each message is either marketing or transactional, and the classification decides who can opt out of it."
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Marketing"
        }), " messages honor topic and channel preferences."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Transactional"
        }), " messages are ones a recipient needs, such as a receipt, a password reset, or a policy update. They skip topic subscriptions and channel unsubscribes, reaching recipients who have opted out of marketing email."]
      }), "\n", _jsx(_components.h2, {
        id: "when-email-cant-be-delivered",
        children: "When email can't be delivered"
      }), "\n", _jsx(_components.p, {
        children: "A bounce or a spam complaint stops future email to that address. This is separate from subscription preferences and from suppression lists:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Their topic and channel subscriptions are untouched."
        }), "\n", _jsx(_components.li, {
          children: "Nothing is added to your suppression lists."
        }), "\n", _jsx(_components.li, {
          children: "It applies to that address on that channel only, so a bounced email address doesn't affect the same person's SMS or a different address on their profile."
        }), "\n"]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "This check is always enforced and can't be overridden, including for transactional messages. Once an address is recorded as undeliverable, contact your Hightouch team to clear it."
        })
      }), "\n", _jsx(_components.h2, {
        id: "how-recipients-unsubscribe",
        children: "How recipients unsubscribe"
      }), "\n", _jsx(_components.p, {
        children: "Every marketing email carries two paths, and both apply to the topic the message was sent under:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "The unsubscribe link in your footer, which opens a preference page where the recipient can manage topics."
        }), "\n", _jsxs(_components.li, {
          children: ["The unsubscribe button the mail client renders itself, from the ", _jsx(_components.code, {
            children: "List-Unsubscribe"
          }), " header."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Add ", _jsx(_components.code, {
          children: "{{ unsubscribe_url }}"
        }), " to your footer and Hightouch generates a per-recipient link. Configure the preference page and the links shown on it under ", _jsx(_components.strong, {
          children: "Settings → Consent"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "unsubscribe-link-variables",
        children: "Unsubscribe link variables"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch generates the unsubscribe link for each recipient at send time. Write one of these variables into your email footer, email body, or SMS text, and choose it by what the recipient should leave:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Variable"
            }), _jsx(_components.th, {
              children: "What the link does"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "{{ topic_unsubscribe_url }}"
              })
            }), _jsx(_components.td, {
              children: "Unsubscribes the recipient from the topic the message was sent under. Other topics are untouched."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "{{ channel_unsubscribe_url }}"
              })
            }), _jsx(_components.td, {
              children: "Unsubscribes the recipient from the whole channel: all marketing email, or all SMS."
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "A topic link opens a page that names the topic and asks the recipient to confirm, with a link to manage their other subscriptions. A channel link asks them to confirm leaving the channel instead."
      }), "\n", _jsx(_components.p, {
        children: "A transactional message has no topic to leave, so all three variables produce a channel link on it. For email, the recipient stops receiving marketing on that channel and still receives transactional messages. For SMS, a channel unsubscribe stops all SMS communications, the same as if the recipient had replied STOP."
      }), "\n", _jsx(_components.p, {
        children: "You can use more than one in a message. A footer can offer \"Unsubscribe from this newsletter\" with the topic link and \"Unsubscribe from all email\" with the channel link."
      }), "\n", _jsx(_components.p, {
        children: "In an SMS, Hightouch replaces the variable with a shortened link. Hightouch fills these variables in for you, so they don't appear in the message's personalization variables and you don't need to supply values for them in an API-triggered send."
      }), "\n", _jsx(_components.h2, {
        id: "keep-your-own-consent-platform",
        children: "Keep your own consent platform"
      }), "\n", _jsx(_components.p, {
        children: "If you manage preferences in a third-party consent tool, you don't have to move off it. Both unsubscribe paths can point at your platform instead of the Hightouch preference page: the footer link and the header your mail client reads."
      }), "\n", _jsx(_components.p, {
        children: "That means building a URL that identifies the recipient, usually from a token or identifier on their profile, which you can write as a variable in your footer. Consent changes then need to reach your warehouse so Hightouch honors them on the next send."
      }), "\n", _jsx(_components.p, {
        children: "Reach out to your Hightouch contact to set this up. They'll work through the URL format and how consent flows back."
      }), "\n", _jsx(_components.h2, {
        id: "double-opt-in",
        children: "Double opt-in"
      }), "\n", _jsx(_components.p, {
        children: "A double opt-in topic requires the recipient to confirm before you can send to it. They stay unmarketable on that topic until they confirm, so a recipient who never confirms receives nothing."
      }), "\n", _jsx(_components.p, {
        children: "You start the flow with an API call. When someone signs up, call the double opt-in endpoint with their address and the topic, and Hightouch sends the confirmation message and records their status."
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-bash",
          children: ["curl -X POST ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'https://api.hightouch.com/api/v1/profiles/subscriptions/double-opt-in'"
          }), " \\\n  -H ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'Authorization: Bearer YOUR_API_KEY'"
          }), " \\\n  -H ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'Content-Type: application/json'"
          }), " \\\n  -d ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'{\n    \"handle\": \"sam@example.com\",\n    \"channel\": \"email\",\n    \"topicId\": \"...\"\n  }'"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Identify the topic with either its ", _jsx(_components.code, {
          children: "topicId"
        }), " or the external identifier you gave it. On email the recipient clicks a confirmation link. On SMS they reply, and one reply confirms every pending SMS topic."]
      }), "\n", _jsx(_components.p, {
        children: "Some jurisdictions require double opt-in. Once enabled for a channel, it can't be turned off."
      }), "\n", _jsx(_components.h2, {
        id: "suppression-lists",
        children: "Suppression lists"
      }), "\n", _jsx(_components.p, {
        children: "Suppression lists are your own exclusions, expressed as audience conditions: hold back this group on this topic, whatever their preferences. Use them for legal holds, regional exclusions, or recipients still managed by another platform during a migration."
      }), "\n", _jsxs(_components.p, {
        children: ["They attach to subscription topics and apply when a campaign builds its audience. See ", _jsx(_components.a, {
          href: "/native-delivery/suppression-lists",
          children: "Suppression lists"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "what-recipients-cant-do",
        children: "What recipients can't do"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Keep separate preferences on a shared address."
          }), " Topic preferences belong to the email address. Two profiles sharing an address share its preferences."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Carry preferences to a new address."
          }), " A recipient who changes address starts at each topic's default. Opt-in topics need a fresh opt-in."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "sms-consent",
        children: "SMS consent"
      }), "\n", _jsx(_components.p, {
        children: "SMS consent has stricter rules. Recipients opt in before you can send, and a STOP reply applies across your whole workspace."
      }), "\n", _jsx(_components.h3, {
        id: "topics-are-always-opt-in",
        children: "Topics are always opt-in"
      }), "\n", _jsx(_components.p, {
        children: "Every SMS topic is opt-in. A recipient receives nothing on a topic until they subscribe to it, and SMS topics have no policy to choose."
      }), "\n", _jsx(_components.h3, {
        id: "stop-and-start",
        children: "STOP and START"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Reply"
            }), _jsx(_components.th, {
              children: "What happens"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "STOP"
              })
            }), _jsx(_components.td, {
              children: "Unsubscribes from the SMS channel and from every SMS topic in your workspace."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "START"
              })
            }), _jsx(_components.td, {
              children: "Resubscribes to the channel only."
            })]
          })]
        })]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "A recipient who replied STOP receives no further SMS, including transactional messages like receipts and password resets. Plan to reach them on another channel."
        })
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/native-delivery/reporting",
            children: "Reporting"
          })
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
